import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  OtherReportFilterSection,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

/**
 * Non-Fuel Stock Statement — one row per product for any period:
 * opening stock + purchases − purchase returns − sales = closing stock.
 * Vendors / products are optional filters; with none selected every product is shown.
 */
const qty = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 3 });
};

const csvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const NonFuelStockStatementReport = (props) => {
  const [meta, setMeta] = useState({ vendors: [], products: [] });
  const [inputValues, setInputValues] = useState({ vendor: [], product: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/product_sales_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Non-Fuel Stock Statement', error.message, 'error'));
  }, []);

  const toggleSelection = (field, id) => {
    const selected = new Set((inputValues[field] || []).map(String));
    const key = String(id);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    setInputValues({ ...inputValues, [field]: Array.from(selected) });
  };

  const setSelection = (field, ids) => {
    setInputValues({ ...inputValues, [field]: (ids || []).map(String) });
  };

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
    vendor: inputValues.vendor,
    product: inputValues.product,
  });

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    return true;
  };

  const loadReport = async () => {
    if (!validateFilters()) return;
    setLoading(true);
    setReport(null);
    try {
      setReport(await fetchReportJson(`${API_URL}/nonfuel_stock_statement`, buildPayload()));
    } catch (error) {
      swal('Non-Fuel Stock Statement', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) return;
    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/nonfuel_stock_statement/pdf`,
        buildPayload(),
        `nonfuel_stock_statement_${inputValues.start_date}_${inputValues.end_date}.pdf`,
      );
    } catch (error) {
      swal('Non-Fuel Stock Statement', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!report) return;
    const lines = [
      [report.pump_name, 'Non-Fuel Stock Statement', `${report.start_date} to ${report.end_date}`],
      [],
      ['Product', 'Unit', 'Opening stock', 'Purchases', 'Purchase value', 'Purchase returns', 'Sales', 'Sales value', 'Closing stock', 'Difference vs system today'],
      ...(report.rows || []).map((r) => [
        r.product_name, r.unit, r.opening_stock, r.purchased_quantity, r.purchase_value,
        r.purchase_return_quantity, r.sales_quantity, r.sales_value, r.closing_stock,
        Number(r.stock_difference_today) !== 0 ? r.stock_difference_today : '',
      ]),
      ['Total', '', '', '', report.totals?.purchase_value, '', '', report.totals?.sales_value, '', ''],
    ];
    if ((report.loose_oil_rows || []).length) {
      lines.push([], ['Loose oil (litres)'], ['Product', 'Barrel size (L)', 'Opening (L)', 'Barrels bought', 'Purchases (L)', 'Purchase value', 'Returns (L)', 'Sales (L)', 'Sales value', 'Closing (L)']);
      report.loose_oil_rows.forEach((r) => lines.push([
        r.product_name, r.barrel_litres, r.opening_stock, r.purchased_barrels, r.purchased_quantity,
        r.purchase_value, r.purchase_return_quantity, r.sales_quantity, r.sales_value, r.closing_stock,
      ]));
    }
    const csv = lines.map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `nonfuel_stock_statement_${report.start_date}_${report.end_date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  };

  const rows = useMemo(() => report?.rows || [], [report]);
  const looseRows = useMemo(() => report?.loose_oil_rows || [], [report]);
  const anySetUpInPeriod = rows.some((r) => r.set_up_in_period);

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Non-Fuel Stock Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <ReportDateRangeFields
                    startDate={inputValues.start_date}
                    endDate={inputValues.end_date}
                    onChange={({ start_date, end_date }) => setInputValues({ ...inputValues, start_date, end_date })}
                  />
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
              <OtherReportFilterSection title="Filters" description="Optional. Leave both empty to show every product.">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="other-report-filter-section-title d-block mb-2">Vendors</label>
                    <OtherReportSearchableCheckList
                      items={meta.vendors || []}
                      selectedIds={inputValues.vendor || []}
                      onToggle={(id) => toggleSelection('vendor', id)}
                      onChange={(ids) => setSelection('vendor', ids)}
                      searchPlaceholder="Search vendors…"
                      getId={(item) => String(item.id)}
                      getLabel={(item) => item.name}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="other-report-filter-section-title d-block mb-2">Products</label>
                    <OtherReportSearchableCheckList
                      items={meta.products || []}
                      selectedIds={inputValues.product || []}
                      onToggle={(id) => toggleSelection('product', id)}
                      onChange={(ids) => setSelection('product', ids)}
                      searchPlaceholder="Search products…"
                      getId={(item) => String(item.id)}
                      getLabel={(item) => item.product_name}
                    />
                  </div>
                </div>
              </OtherReportFilterSection>
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating stock statement" />}
              {report && !loading && (
                <div className="form-wrap balace-sheetwrap">
                  <div className="row mb-3">
                    <div className="col-md">
                      <h2 className="table-title mb-1">{report.pump_name}</h2>
                      <h4 className="table-title mb-1">Non-Fuel Stock Statement</h4>
                      <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                      <button type="button" className="other-report-btn other-report-btn-secondary mt-2" onClick={downloadCsv}>
                        Download Excel (CSV)
                      </button>
                      <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                    </div>
                  </div>
                  <div className="other-report-table-wrap">
                    <table className="other-report-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Unit</th>
                          <th>Opening stock</th>
                          <th>Purchases</th>
                          <th>Purchase value</th>
                          <th>Purchase returns</th>
                          <th>Sales</th>
                          <th>Sales value</th>
                          <th>Closing stock</th>
                          <th title="Current system stock minus purchases-and-sales stock. Non-zero means the stock was changed by an edit or correction.">Difference vs system today</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.product_id}>
                            <td>{r.product_name}{r.set_up_in_period ? ' *' : ''}</td>
                            <td>{r.unit}</td>
                            <td className="report-amount">{qty(r.opening_stock)}</td>
                            <td className="report-amount">{qty(r.purchased_quantity)}</td>
                            <td className="report-amount">{reportNumberFormat(r.purchase_value)}</td>
                            <td className="report-amount">{qty(r.purchase_return_quantity)}</td>
                            <td className="report-amount">{qty(r.sales_quantity)}</td>
                            <td className="report-amount">{reportNumberFormat(r.sales_value)}</td>
                            <td className="report-amount" style={r.negative_stock ? { color: '#b42318', fontWeight: 600 } : undefined}>{qty(r.closing_stock)}</td>
                            <td className="report-amount" style={Number(r.stock_difference_today) !== 0 ? { color: '#b42318' } : undefined}>
                              {Number(r.stock_difference_today) !== 0 ? qty(r.stock_difference_today) : ''}
                            </td>
                          </tr>
                        ))}
                        {rows.length > 0 && (
                          <tr className="other-report-totals-row">
                            <td colSpan={4}>Total ({report.totals?.products} products)</td>
                            <td className="report-amount">{reportNumberFormat(report.totals?.purchase_value)}</td>
                            <td colSpan={2} />
                            <td className="report-amount">{reportNumberFormat(report.totals?.sales_value)}</td>
                            <td colSpan={2} />
                          </tr>
                        )}
                        {rows.length === 0 && (
                          <tr><td colSpan={10}>No products match these filters.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {looseRows.length > 0 && (
                    <>
                      <h5 className="mt-4 mb-2">Loose oil (litres)</h5>
                      <div className="other-report-table-wrap">
                        <table className="other-report-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Barrel size (L)</th>
                              <th>Opening (L)</th>
                              <th>Barrels bought</th>
                              <th>Purchases (L)</th>
                              <th>Purchase value</th>
                              <th>Returns (L)</th>
                              <th>Sales (L)</th>
                              <th>Sales value</th>
                              <th>Closing (L)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {looseRows.map((r) => (
                              <tr key={`loose-${r.product_id}`}>
                                <td>{r.product_name}</td>
                                <td className="report-amount">{qty(r.barrel_litres)}</td>
                                <td className="report-amount" style={Number(r.opening_stock) < 0 ? { color: '#b42318' } : undefined}>{qty(r.opening_stock)}</td>
                                <td className="report-amount">{qty(r.purchased_barrels)}</td>
                                <td className="report-amount">{qty(r.purchased_quantity)}</td>
                                <td className="report-amount">{reportNumberFormat(r.purchase_value)}</td>
                                <td className="report-amount">{qty(r.purchase_return_quantity)}</td>
                                <td className="report-amount">{qty(r.sales_quantity)}</td>
                                <td className="report-amount">{reportNumberFormat(r.sales_value)}</td>
                                <td className="report-amount" style={r.negative_stock ? { color: '#b42318', fontWeight: 600 } : undefined}>{qty(r.closing_stock)}</td>
                              </tr>
                            ))}
                            <tr className="other-report-totals-row">
                              <td colSpan={5}>Total ({report.loose_oil_totals?.products} products)</td>
                              <td className="report-amount">{reportNumberFormat(report.loose_oil_totals?.purchase_value)}</td>
                              <td colSpan={2} />
                              <td className="report-amount">{reportNumberFormat(report.loose_oil_totals?.sales_value)}</td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {(anySetUpInPeriod || (report.notes || []).length > 0) && (
                    <div className="mt-3 small text-muted">
                      {anySetUpInPeriod && <div>* Product set up during the period: its opening stock is the stock entered when it was set up.</div>}
                      {(report.notes || []).map((note) => <div key={note}>• {note}</div>)}
                    </div>
                  )}
                </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default NonFuelStockStatementReport;
